import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

Modal.setAppElement('#root');

const BankReconciliation = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [bankTransactions, setBankTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');

  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('csv');
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);

  const [journalEntries, setJournalEntries] = useState([]);

  const [isFetchingTransactions, setIsFetchingTransactions] = useState(true);
  const [hasFetchedTransactions, setHasFetchedTransactions] = useState(false);

  const [reconcilingItems, setReconcilingItems] = useState([]);

  const bankTransactionsCollectionRef = collection(db, "bankTransactions");
  const journalEntriesCollectionRef = collection(db, "journalEntries");
  const accountsCollectionRef = collection(db, "accounts");

  useEffect(() => {
    if (user && userData) {
      fetchBankAccounts();
      fetchAccounts();
    }
  }, [user, userData]);

  useEffect(() => {
    if (selectedBankAccount) {
      fetchBankTransactions();
      fetchJournalEntries();
    }
  }, [selectedBankAccount]);

  useEffect(() => {
    const filtered = bankTransactions.filter((tx) =>
      tx.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTransactions(filtered);
  }, [searchTerm, bankTransactions]);

  useEffect(() => {
    if (bankTransactions.length > 0) {
      identifyReconcilingItems();
    }
  }, [bankTransactions]);

  const fetchAccounts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(accountsCollectionRef);
      const data = await getDocs(q);
      const accountList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setAccounts(accountList);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setError("Failed to fetch accounts");
      setShowModal(true);
    }
  };

  const fetchBankAccounts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(accountsCollectionRef, where("type", "==", "Asset"));
      const data = await getDocs(q);
      const accountList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setBankAccounts(accountList);
      if (accountList.length > 0) {
        setSelectedBankAccount(accountList[0].id);
      }
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      setError("Failed to fetch bank accounts");
      setShowModal(true);
    }
  };

  const fetchBankTransactions = async () => {
    if (!user?.uid || !selectedBankAccount) return;
    
    setIsFetchingTransactions(true);
    try {
      const q = query(bankTransactionsCollectionRef, where("bankAccountId", "==", selectedBankAccount));
      const data = await getDocs(q);
      const txList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setBankTransactions(txList);
      setHasFetchedTransactions(true);
    } catch (error) {
      console.error("Error fetching bank transactions:", error);
      setError("Failed to fetch bank transactions");
      setShowModal(true);
    } finally {
      setIsFetchingTransactions(false);
    }
  };

  const fetchJournalEntries = async () => {
    if (!user?.uid || !selectedBankAccount) return;
    try {
      const q = query(journalEntriesCollectionRef, where("matched", "==", false));
      const data = await getDocs(q);
      const entryList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setJournalEntries(entryList);
    } catch (error) {
      console.error("Error fetching journal entries:", error);
      setError("Failed to fetch journal entries");
      setShowModal(true);
    }
  };

  const identifyReconcilingItems = () => {
    const uncleared = bankTransactions.filter(tx => !tx.cleared);
    setReconcilingItems(uncleared);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };
  
  const handleFileTypeChange = (e) => {
    setFileType(e.target.value);
  };
  
  const processImport = async () => {
    if (!file || !selectedBankAccount) {
      setError("Please select a file and bank account");
      setShowModal(true);
      return;
    }
  
    setUpdate(true);
    setImportStatus('Processing file...');
    setImportProgress(0);
  
    try {
      let txData = [];
  
      if (fileType === 'excel') {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        txData = XLSX.utils.sheet_to_json(worksheet);
      } else if (fileType === 'csv') {
        const text = await file.text();
        const result = Papa.parse(text, {
          header: true,
          skipEmptyLines: true
        });
        txData = result.data;
      } else {
        throw new Error("OFX/QFX import not implemented. Use CSV/Excel or add parser.");
      }
  
      if (txData.length === 0) {
        throw new Error("No transaction data found in the file");
      }
  
      setImportStatus(`Importing ${txData.length} transactions...`);
      
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < txData.length; i++) {
        try {
          const tx = txData[i];
          
          if (!tx.date || !tx.amount || !tx.description) {
            errorCount++;
            continue;
          }

          const txDataToAdd = {
            date: tx.date,
            amount: parseFloat(tx.amount),
            description: tx.description,
            bankAccountId: selectedBankAccount,
            cleared: false,
            matchedTo: null,
            userId: user?.uid || '',
            createdAt: Timestamp.now()
          };
  
          await addDoc(bankTransactionsCollectionRef, txDataToAdd);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error importing transaction ${i + 1}:`, error);
        }
  
        setImportProgress(Math.round(((i + 1) / txData.length) * 100));
      }
  
      setImportStatus(`Import complete: ${successCount} successful, ${errorCount} failed`);
      setSuccessMessage(`Import completed: ${successCount} transactions added`);
      fetchBankTransactions();
    } catch (error) {
      setError(`Import failed: ${error.message}`);
      setShowModal(true);
    } finally {
      setUpdate(false);
      setFile(null);
      setTimeout(() => {
        setIsImportModalOpen(false);
        setImportProgress(0);
        setImportStatus('');
      }, 2000);
    }
  };

  const confirmClearTransaction = (txToClear) => {
    setCurrentTransaction(txToClear);
    setIsClearModalOpen(true);
  };

  const handleClearTransaction = async (id) => {
    setUpdate(true);
    try {
      const txDoc = doc(db, "bankTransactions", id);
      await updateDoc(txDoc, { cleared: true });
      setSuccessMessage("Transaction cleared successfully");
      fetchBankTransactions();
    } catch (error) {
      setError("Failed to clear transaction");
    } finally {
      setUpdate(false);
      setIsClearModalOpen(false);
      setShowModal(true);
    }
  };

  const openMatchModal = (txToMatch) => {
    setCurrentTransaction(txToMatch);
    setIsMatchModalOpen(true);
  };

  const handleMatchTransaction = async (journalEntryId) => {
    setUpdate(true);
    try {
      const bankTxDoc = doc(db, "bankTransactions", currentTransaction.id);
      await updateDoc(bankTxDoc, { matchedTo: journalEntryId });
      const journalEntryDoc = doc(db, "journalEntries", journalEntryId);
      await updateDoc(journalEntryDoc, { matched: true });
      setSuccessMessage("Transaction matched successfully");
      fetchBankTransactions();
      fetchJournalEntries();
      setIsMatchModalOpen(false);
    } catch (error) {
      setError("Failed to match transaction");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Cashier', 'Accountant', 'Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingTransactions && !hasFetchedTransactions) {
      return <p className="loading-text">Loading transactions...</p>;
    }
    
    if (filteredTransactions.length === 0) {
      if (searchTerm) {
        return <p className="loading-text">No transactions match your search.</p>;
      } else {
        return <p className="loading-text">No bank transactions found. Import your first statement.</p>;
      }
    }
    
    return filteredTransactions.map((txItem) => (
      <div key={txItem.id} className="class-item">
        <div className="column gap-1">
          <p><strong>{txItem.description}</strong><br />
            <span>Amount: ${txItem.amount}</span>&nbsp;-&nbsp;
            <span>Date: {txItem.date}</span><br />
            <span>Matched: {txItem.matchedTo ? 'Yes' : 'No'}</span><br />
            <span className="size5 font-light-blue-cash bold">{txItem.cleared ? 'Cleared' : 'Uncleared'}</span>
          </p>                        
        </div>
        <div className="row align-center justify-center gap-15">
          {!txItem.cleared && (
            <button className="list-btn" onClick={() => confirmClearTransaction(txItem)}>
              <i className="fas fa-check"></i>
            </button>
          )}
          {!txItem.matchedTo && (
            <button className="list-btn" onClick={() => openMatchModal(txItem)}>
              <i className="fas fa-link"></i>
            </button>
          )}
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Bank Reconciliation</h2>
            <Link to="#" className="home-link" onClick={() => fetchBankTransactions()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <select
              className="input-field1"
              style={{marginTop: '20px', marginBottom: '-10px'}}
              value={selectedBankAccount}
              onChange={(e) => setSelectedBankAccount(e.target.value)}
            >
              {bankAccounts.map(acc => (
                <option key={acc.id} value={acc.id}>{acc.name}</option>
              ))}
            </select>

            <div className="row justify-center align-center gap-15 add-buttons margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsImportModalOpen(true)}>Import Statement</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by description"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>

            <div className="reconciling-section column align-center gap-1 margin-top-cashflow1">
              <h3>Reconciling Items</h3>
              {reconcilingItems.length > 0 ? (
                reconcilingItems.map(item => (
                  <p key={item.id}>{item.description} - ${item.amount} (Uncleared)</p>
                ))
              ) : (
                <p>No reconciling items.</p>
              )}
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal
        isOpen={isImportModalOpen}
        onRequestClose={() => {
          setIsImportModalOpen(false);
          setFile(null);
          setImportProgress(0);
          setImportStatus('');
        }}
        contentLabel="Import Bank Statement"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Import Bank Statement</h2>
        <div className="modal-form">
          <div style={{marginBottom: '5px'}}>
            <label>
              <input
                type="radio"
                name="fileType"
                value="csv"
                checked={fileType === 'csv'}
                onChange={handleFileTypeChange}
              /> CSV File (.csv)
            </label>
            <label>
              <input
                type="radio"
                name="fileType"
                value="excel"
                checked={fileType === 'excel'}
                onChange={handleFileTypeChange}
              /> Excel File (.xlsx)
            </label>
            <label>
              <input
                type="radio"
                name="fileType"
                value="ofx"
                checked={fileType === 'ofx'}
                onChange={handleFileTypeChange}
              /> OFX/QFX File
            </label>
          </div>
          
          <input
            type="file"
            accept={fileType === 'excel' ? '.xlsx, .xls' : fileType === 'csv' ? '.csv' : '.ofx, .qfx'}
            onChange={handleFileChange}
            className="input-field1"
          />
          
          {importStatus && (
            <div>
              <p>{importStatus}</p>
              {importProgress > 0 && (
                <div style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '5px' }}>
                  <div
                    style={{
                      width: `${importProgress}%`,
                      height: '20px',
                      backgroundColor: '#4CAF50',
                      borderRadius: '5px',
                      textAlign: 'center',
                      lineHeight: '20px',
                      color: 'white'
                    }}
                  >
                    {importProgress}%
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="row align-center justify-center gap-15">
            <button
              type="button"
              className="login-button caps"
              onClick={() => {
                setIsImportModalOpen(false);
                setFile(null);
                setImportProgress(0);
                setImportStatus('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="login-button caps"
              onClick={processImport}
              disabled={!file || update}
            >
              Import
            </button>
          </div>
          
          <div className="import-instructions">
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED FIELDS:</strong> date, amount, description</p>
            <p><strong>FOR EXCEL/CSV:</strong> First row should contain column headers</p>            
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isClearModalOpen}
        onRequestClose={() => setIsClearModalOpen(false)}
        contentLabel="Clear Transaction"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Mark as Cleared</h2>
        {currentTransaction && (
          <div>
            <p>Are you sure you want to mark <strong>{currentTransaction.description}</strong> as cleared?</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsClearModalOpen(false)}>Cancel</button>
              <button className="login-button" type="button" onClick={() => handleClearTransaction(currentTransaction.id)}>Clear</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isMatchModalOpen}
        onRequestClose={() => setIsMatchModalOpen(false)}
        contentLabel="Match Transaction"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Match to Journal Entry</h2>
        {currentTransaction && (
          <div>
            <p>Match bank transaction: <strong>{currentTransaction.description}</strong> (${currentTransaction.amount})</p>
            <h4>Unmatched Journal Entries:</h4>
            {journalEntries.filter(entry => entry.debitAccountId === selectedBankAccount || entry.creditAccountId === selectedBankAccount).length > 0 ? (
              journalEntries
                .filter(entry => entry.debitAccountId === selectedBankAccount || entry.creditAccountId === selectedBankAccount)
                .map(entry => (
                  <div key={entry.id} className="class-item">
                    <p>{entry.description} - ${entry.amount}<br />
                      <span>Debit: {accounts.find(a => a.id === entry.debitAccountId)?.name || 'Unknown'}</span><br />
                      <span>Credit: {accounts.find(a => a.id === entry.creditAccountId)?.name || 'Unknown'}</span>
                    </p>
                    <button className="login-button" onClick={() => handleMatchTransaction(entry.id)}>Match</button>
                  </div>
                ))
            ) : (
              <p>No unmatched journal entries available for this bank account.</p>
            )}
            <button className="login-button" type="button" onClick={() => setIsMatchModalOpen(false)}>Cancel</button>
          </div>
        )}
      </Modal>
    </>
  );
};

export default BankReconciliation;